import _ from 'underscore';
import React from 'react';
import { odata } from '@bingads-webui/http-util';
import { showGoogleLoginPopup, updateTaskItem, prepareGoogleImportTaskItemBodyForPmaxImportRecommendationRunnow } from '@bingads-webui-campaign-legacy/scheduled-import/view/utils/google-import-utils';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { INotification, TargetComponent, NotificationLevel, DismissMode, ScopeLevel, IContext } from '../../types';
import { i18nMap, shouldRender, isAvailable, isBillingLinkPermission } from '../../utils';
import {
  INotificationItem,
  ITokenExpirationItemList,
  ITokenInvalidItemList,
  IExtractedNotifications,
  IPMaxImportRecommendationItemList,
  IImportTaskItem,
} from '../../types/import.notification.types';

/**
 * ManualTest:
 * 1. Prepare fiddler mock data/real failed import
 * 2. Lauch site with fiddler opened
 * 3. Check the notificaiton in UNQ
 * 4. Go to import schedule page
 */

const GOOGLE_IMPORT_TYPE = 'Google';
const FACEBOOK_IMPORT_TYPE = 'Facebook';

export interface IImportNotifications{
  '@odata.context': string,
  AccountId: number,
  TokenExpiration: ITokenExpirationItemList,
  TokenInvalid: ITokenInvalidItemList,
  PMaxImportRecommendation?: IPMaxImportRecommendationItemList,
}

export class ImportNotification {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
    this.availableScopes = [ScopeLevel.account];
  }

  async getImportNotifications(scopeLevel?: ScopeLevel) {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    // permissions
    const permissions = this.context.getCurrentPermissions();
    const { IsPmaxImportRecommendationNotificationEnabled, IsImportAutoMinimumBidEnabled } = permissions;
    const { TokenInvalidImportNotification, ImportNotificationForRemoveMinimumBid, MetaImportUpsellNotification } = permissions.dynamic; // TODO: remove GA pilot

    // Only get this notification under available scope
    if (!isAvailable(this.context.getCurrentScope(), this.availableScopes)) {
      return [];
    }

    // Only get this notification if change scope is bigger than account level
    if (!shouldRender(scopeLevel || ScopeLevel.account, this.context.getChangeScope())) {
      return [];
    }

    // Get import notifications
    const importNotifications: IImportNotifications = await this.getNotifications();
    const notifications: INotification[] = [];

    // Token expiration notification
    if (this.hasAnyTokenExpiratioinNotifications(importNotifications)) {
      // Get token expiration notifications
      const tokenExpirationNotifications: IExtractedNotifications = this.extractNotifications(importNotifications, 'TokenExpiration');

      // Google import token expiration notification
      if (tokenExpirationNotifications.GoogleAPIImport && tokenExpirationNotifications.GoogleAPIImport !== 0) {
        notifications.push(this.getGoogleTokenExpirationNotification(tokenExpirationNotifications.GoogleAPIImport));
      }

      // Facebook import token expiration notification
      if (tokenExpirationNotifications.FacebookImport && tokenExpirationNotifications.FacebookImport !== 0) {
        notifications.push(this.getFacebookTokenExpirationNotification(tokenExpirationNotifications.FacebookImport));
      }
    }

    // Token invalid notification
    if (TokenInvalidImportNotification && this.hasAnyTokenInvalidNotifications(importNotifications)) {
      // Get token invalid notifications
      const tokenInvalidNotifications: IExtractedNotifications = this.extractNotifications(importNotifications, 'TokenInvalid');

      // Google import token invalid notification
      if (tokenInvalidNotifications.GoogleAPIImport && tokenInvalidNotifications.GoogleAPIImport !== 0) {
        notifications.push(this.getGoogleTokenInvalidNotification(tokenInvalidNotifications.GoogleAPIImport));
      }

      // Facebook import token invalid notification
      if (tokenInvalidNotifications.FacebookImport && tokenInvalidNotifications.FacebookImport !== 0) {
        notifications.push(this.getFacebookTokenInvalidNotification(tokenInvalidNotifications.FacebookImport));
      }
    }

    // Pmax import recommendation notification
    if (IsPmaxImportRecommendationNotificationEnabled && this.hasAnyPmaxImportRecommendationNotifications(importNotifications)) {
      // Get pmax import recommendation notifications
      const pmaxImportRecommendationNotifications: IPMaxImportRecommendationItemList = importNotifications.PMaxImportRecommendation;

      // Pmax import notification fail notification
      if (pmaxImportRecommendationNotifications.Failed && pmaxImportRecommendationNotifications.Failed.length > 0) {
        this.getPMaxImportRecommendationFailNotification(pmaxImportRecommendationNotifications.Failed[0].Tasks, notifications);
      }

      // Pmax import notification complete notification
      if (pmaxImportRecommendationNotifications.Completed && pmaxImportRecommendationNotifications.Completed.length > 0) {
        this.getPMaxImportRecommendationCompleteNotification(pmaxImportRecommendationNotifications.Completed[0].Tasks, notifications);
      }

      // Pmax import notification complete without pmax campaign imported notification
      if (pmaxImportRecommendationNotifications.CompletedWithoutPMax && pmaxImportRecommendationNotifications.CompletedWithoutPMax.length > 0) {
        this.getPMaxImportRecommendationCompletedWithoutPmaxNotification(pmaxImportRecommendationNotifications.CompletedWithoutPMax[0].Tasks, notifications);
      }
    }
    if (ImportNotificationForRemoveMinimumBid && !IsImportAutoMinimumBidEnabled) {
      notifications.push(this.getImportAutoMinimumBidNotification());
    }
    if (MetaImportUpsellNotification) {
      notifications.push(this.getMetaImportUpsellNotification());
    }

    return _.compact(notifications);
  }

  async getNotifications(): Promise<IImportNotifications> {
    const { aid, cid } = this.context.getCurrentScope().values;
    const url = `/ODataApi/Campaign/V2/Customers(${cid})/Accounts(${aid})/ImportNotifications`;

    return (await odata.get({
      url,
      oDataToken: this.context.getAuthToken(),
    })) as IImportNotifications;
  }

  getGoogleTokenExpirationNotification(count): INotification {
    const form = count > 1 ? 'Plural' : 'Singular';

    return {
      id: 'GoogleImportTokenExpiration',
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${GOOGLE_IMPORT_TYPE}_ImportTaskTokenExpirationInfoText_Title`]),
      message: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${GOOGLE_IMPORT_TYPE}_ImportTaskTokenExpirationInfoText_${form}_Message`], { count }),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_GoToImportSchedulePage),
          action: () => {
            this.redirectToImportCampaignScheduleWithImportType('googleimport');
          },
          callbackType: 'closeQueue',
          extraClassNames: ['google-import-token-expiration-redirect-button'],
        },
      ],
    };
  }

  getFacebookTokenExpirationNotification(count): INotification {
    const form = count > 1 ? 'Plural' : 'Singular';

    return {
      id: 'FacebookImportTokenExpiration',
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${FACEBOOK_IMPORT_TYPE}_ImportTaskTokenExpirationInfoText_Title`]),
      message: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${FACEBOOK_IMPORT_TYPE}_ImportTaskTokenExpirationInfoText_${form}_Message`], { count }),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_GoToImportSchedulePage),
          action: () => {
            this.redirectToImportCampaignScheduleWithImportType('facebookimport');
          },
          callbackType: 'closeQueue',
          extraClassNames: ['facebook-import-token-expiration-redirect-button'],
        },
      ],
    };
  }

  getGoogleTokenInvalidNotification(count): INotification {
    const form = count > 1 ? 'Plural' : 'Singular';

    return {
      id: 'GoogleImportTokenInvalid',
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${GOOGLE_IMPORT_TYPE}_ImportTaskTokenInvalidInfoText_Title`]),
      message: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${GOOGLE_IMPORT_TYPE}_ImportTaskTokenInvalidInfoText_${form}_Message`], { count }),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_GoToImportSchedulePage),
          action: () => {
            this.redirectToImportCampaignScheduleWithImportType('googleimport');
          },
          callbackType: 'closeQueue',
          extraClassNames: ['google-import-token-invalid-redirect-button'],
        },
        // TODO: use real help id when it is prepared
        // {
        //   text: this.i18n.getString(i18nMap.LearnMore),
        //   extraClassNames: ['HelpTopic'],
        //   extraAttrs: {
        //     query: 'help:ext51085',
        //   },
        // },
      ],
    };
  }

  getFacebookTokenInvalidNotification(count): INotification {
    const form = count > 1 ? 'Plural' : 'Singular';

    return {
      id: 'FacebookImportTokenInvalid',
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${FACEBOOK_IMPORT_TYPE}_ImportTaskTokenInvalidInfoText_Title`]),
      message: this.i18n.getString(i18nMap[`UnifiedNotificationQueue_${FACEBOOK_IMPORT_TYPE}_ImportTaskTokenInvalidInfoText_${form}_Message`], { count }),
      actionList: [
        {
          text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_GoToImportSchedulePage),
          action: () => {
            this.redirectToImportCampaignScheduleWithImportType('facebookimport');
          },
          callbackType: 'closeQueue',
          extraClassNames: ['facebook-import-token-invalid-redirect-button'],
        },
        // TODO: use real help id when it is prepared
        // {
        //   text: this.i18n.getString(i18nMap.LearnMore),
        //   extraClassNames: ['HelpTopic'],
        //   extraAttrs: {
        //     query: 'help:ext51085',
        //   },
        // },
      ],
    };
  }

  getPMaxImportRecommendationFailNotification(tasks, notifications) {
    _.each(tasks, (task: IImportTaskItem) => {
      notifications.push({
        id: `PMaxImportRecommendationFail-${task.TaskId}`,
        condition: true,
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.session,
        severity: NotificationLevel.Warning,
        scopeLevel: ScopeLevel.account,
        availableScopes: this.availableScopes,
        title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_Fail_InfoText_Title),
        message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_Fail_InfoText_Message, { username: task.GoogleUserEmail }),
        actionList: [
          {
            text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_SignInToGoogleAds),
            action: () => {
              this.signInToGoogleAdsAndTriggerRunnowImport({ taskId: task.TaskId });
            },
            callbackType: 'closeQueue',
            extraClassNames: ['pmax-import-recommendation-fail-sign-in-button'],
          },
        ],
      });
    });

    return notifications;
  }

  getPMaxImportRecommendationCompleteNotification(tasks, notifications) {
    _.each(tasks, (task: IImportTaskItem) => {
      notifications.push({
        id: `PMaxImportRecommendationComplete-${task.TaskId}`,
        condition: true,
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.permanent,
        severity: NotificationLevel.Info,
        scopeLevel: ScopeLevel.account,
        availableScopes: this.availableScopes,
        title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_Complete_InfoText_Title),
        message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_Complete_InfoText_Message),
        actionList: [
          {
            text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ViewPerformanceMaxCampaigns),
            action: async () => {
              await this.redirectToPerformanceMaxCampaigns();
            },
            callbackType: 'closeQueue',
            extraClassNames: ['pmax-import-recommendation-complete-redirect-button'],
          },
        ],
      });
    });

    return notifications;
  }

  getPMaxImportRecommendationCompletedWithoutPmaxNotification(tasks, notifications) {
    _.each(tasks, (task: IImportTaskItem) => {
      notifications.push({
        id: `PMaxImportRecommendationCompletedWithoutPmax-${task.TaskId}`,
        condition: true,
        target: TargetComponent.UnifiedQueue,
        dismissMode: DismissMode.permanent,
        severity: NotificationLevel.Info,
        scopeLevel: ScopeLevel.account,
        availableScopes: this.availableScopes,
        title: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_CompleteWithoutPmax_InfoText_Title),
        message: this.i18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_CompleteWithoutPmax_InfoText_Message),
        actionList: [
          {
            text: this.i18n.getString(i18nMap.UnifiedNotificationQueue_Action_ViewCampaigns),
            action: async () => {
              await this.redirectToAllCampaigns();
            },
            callbackType: 'closeQueue',
            extraClassNames: ['pmax-import-recommendation-complete-without-pmax-redirect-button'],
          },
        ],
      });
    });

    return notifications;
  }

  getImportAutoMinimumBidNotification() {
    const startdate = new Date('2025-3-24');
    return {
      condition: true,
      id: 'ImportAutoMinimumBid',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_("We're changing the way we import low-bid ad groups")),
      message: this.i18n.getString(
        _TL_("Starting on {{startdate}}, we'll automatically raise the bids of ad groups whose bids are below the Microsoft Advertising minimum to the Microsoft Advertising minimum. This is an effort to ensure that your campaigns are imported with as few issues as possible."),
        {
          startdate: startdate.toLocaleDateString(),
        }
      ),
      actionList: [
        {
          text: this.i18n.getString(_TL_('View my scheduled imports')),
          action: () => {
            this.redirectToImportCampaignSchedule();
          },
          callbackType: 'closeQueue',
          extraClassNames: ['google-import-auto-minimum-bid-redirect-button'],
        },
      ],
    };
  }

  getMetaImportUpsellNotification() {
    return {
      condition: true,
      id: 'MetaImportupsell',
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.permanent,
      severity: NotificationLevel.Info,
      scopeLevel: ScopeLevel.account,
      title: this.i18n.getString(_TL_('Effortlessly bring your ads to Microsoft Advertising with Meta Import')),
      message: this.i18n.getString(_TL_('With Microsoft Advertising, you can engage with unique audiences, leverage our audience signals for better conversions and ROI, and show your ads across the Microsoft Advertising Network with ease.')),
      actionList: [
        {
          text: this.i18n.getString(_TL_('Set up Meta Import')),
          action: () => {
            this.redirectToMetaImportWithImportSrc('metaimportnotification');
          },
          callbackType: 'closeQueue',
          extraClassNames: ['meta-import-redirect-button'],
        },
        {
          text: this.i18n.getString(i18nMap.LearnMore),
          extraClassNames: ['HelpTopic'],
          extraAttrs: {
            query: 'help:app54769',
          },
        },
      ],
    };
  }

  getRedirectLinkToImportCampaignScheduleWithImportType(importType) {
    return this.context.route.getImportScheduleWithImportTypePath(importType);
  }

  redirectToImportCampaignScheduleWithImportType(importType) {
    this.context.route.goImportScheduleWithImportType(importType);
  }

  redirectToImportCampaignSchedule() {
    this.context.route.goImportSchedule();
  }

  async redirectToAllCampaigns() {
    await this.context.route.goAllCampaigns();
  }

  async redirectToPerformanceMaxCampaigns() {
    await this.context.route.goAllCampaigns('pmax');
  }

  redirectToMetaImportWithImportSrc(importsrc) {
    return this.context.route.goMetaimportWithImportSrc(importsrc);
  }

  // sign in to Google Ads and trigger a runnow import task based on previous import task
  signInToGoogleAdsAndTriggerRunnowImport({ taskId }) {
    const newI18n = this.i18n;
    const getSuccessMsg = ({ logMsg, newTaskItem }) => (
      <ReactTemplate
        template={newI18n.getString(i18nMap.UnifiedNotificationQueue_PmaxImportRecommendation_SignIn_And_Trigger_Runnow_Import_Success_Message)}
        model={{
          redirectLink: text => (
            <a
              href={this.getRedirectLinkToImportCampaignScheduleWithImportType('googleimport')}
              onClick={() => {
                logMsg(`Redirect_To_Import_Schedule_And_History, newTaskId: ${newTaskItem.Id}`);
              }}
            >
              {text}
            </a>
          ),
        }}
      />
    );

    return showGoogleLoginPopup({
      newI18n,
      afterSignInCallback: ({ sessionId }) => updateTaskItem({
        sessionId,
        taskId,
        newI18n,
        prepareTaskItemBody: prepareGoogleImportTaskItemBodyForPmaxImportRecommendationRunnow,
        getSuccessMsg,
      }),
    });
  }

  hasAnyTokenExpiratioinNotifications(importNotificationResult: IImportNotifications) {
    return importNotificationResult.TokenExpiration &&
      (!_.isEmpty(importNotificationResult.TokenExpiration.Expired) || !_.isEmpty(importNotificationResult.TokenExpiration.Expiring));
  }

  hasAnyTokenInvalidNotifications(importNotificationResult: IImportNotifications) {
    return importNotificationResult.TokenInvalid && (!_.isEmpty(importNotificationResult.TokenInvalid.Invalid));
  }

  hasAnyPmaxImportRecommendationNotifications(importNotificationResult: IImportNotifications) {
    return importNotificationResult.PMaxImportRecommendation && (
      !_.isEmpty(importNotificationResult.PMaxImportRecommendation.Failed) ||
      !_.isEmpty(importNotificationResult.PMaxImportRecommendation.Completed) ||
      !_.isEmpty(importNotificationResult.PMaxImportRecommendation.CompletedWithoutPMax)
    );
  }

  extractNotifications(importNotifications: IImportNotifications, notificationType) {
    let allTokenExipationList = [];

    // Extract and combine data
    if (notificationType === 'TokenExpiration') {
      allTokenExipationList = [].concat(importNotifications.TokenExpiration.Expiring).concat(importNotifications.TokenExpiration.Expired);
    } else if (notificationType === 'TokenInvalid') {
      allTokenExipationList = importNotifications.TokenInvalid.Invalid;
    }

    const tokenExpirationNotifications = {};

    // Calculate the sum by import type
    _.each(allTokenExipationList, (item: INotificationItem) => {
      if (tokenExpirationNotifications[item.ImportType]) {
        tokenExpirationNotifications[item.ImportType] += item.Count;
      } else {
        tokenExpirationNotifications[item.ImportType] = item.Count;
      }
    });

    return tokenExpirationNotifications as IExtractedNotifications;
  }
}
